import React, { useState, useEffect, useCallback } from 'react';
import { db, collection, getDocs } from '../firebase';
import { useTeam } from '../contexts/TeamContext';
import { buildRoster, calculateRosterStats } from '../utils/roster';
import '../App.css';

const Leaderboard = () => {
  const { teamId, teamData, loading: teamLoading } = useTeam();
  const [players, setPlayers] = useState([]);
  const [sortedPlayers, setSortedPlayers] = useState([]);
  const [sortType, setSortType] = useState('mvpPoints');
  const [sortDir, setSortDir] = useState('desc');
  const [openModal, setOpenModal] = useState(null);

  useEffect(() => {
    if (!teamId) return;

    const loadStats = async () => {
      try {
        const [playersSnapshot, membersSnapshot, matchesSnapshot] = await Promise.all([
          getDocs(collection(db, 'teams', teamId, 'players')),
          getDocs(collection(db, 'teams', teamId, 'members')),
          getDocs(collection(db, 'teams', teamId, 'matches')),
        ]);

        const roster = buildRoster(
          playersSnapshot.docs.map((d) => ({ id: d.id, ...d.data() })),
          membersSnapshot.docs.map((d) => ({ id: d.id, ...d.data() }))
        );
        const matches = matchesSnapshot.docs.map((d) => d.data());

        setPlayers(calculateRosterStats(roster, matches));
      } catch (error) {
        console.error('Greska pri dohvacanju podataka:', error);
      }
    };

    loadStats();
  }, [teamId]);

  const getTopMVPs = (list) =>
    [...list].sort((a, b) => Number(b.mvpPoints || 0) - Number(a.mvpPoints || 0));

  const getTopScorers = (list) =>
    [...list].sort((a, b) => Number(b.goals || 0) - Number(a.goals || 0));

  const getTopAssists = (list) =>
    [...list].sort((a, b) => Number(b.assists || 0) - Number(a.assists || 0));

  const getTopMvpCount = (list) =>
    [...list].sort((a, b) => Number(b.mvpCount || 0) - Number(a.mvpCount || 0));

  const sortLeaderboard = useCallback((list, type, dir) => {
    const sorted = [...list].sort((a, b) => {
      const aVal = type === 'successRate' ? parseFloat(a[type] || 0) : Number(a[type] || 0);
      const bVal = type === 'successRate' ? parseFloat(b[type] || 0) : Number(b[type] || 0);
      return dir === 'asc' ? aVal - bVal : bVal - aVal;
    });
    setSortedPlayers(sorted);
  }, []);

  const handleSort = (col) => {
    const newDir = sortType === col && sortDir === 'desc' ? 'asc' : 'desc';
    setSortType(col);
    setSortDir(newDir);
    sortLeaderboard(players, col, newDir);
  };

  useEffect(() => {
    if (players.length === 0) {
      setSortedPlayers([]);
      return;
    }
    sortLeaderboard(players, sortType, sortDir);
  }, [players, sortType, sortDir, sortLeaderboard]);

  useEffect(() => {
    if (!openModal) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setOpenModal(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [openModal]);

  if (teamLoading) {
    return <div className="loading-screen">Ucitavanje tima...</div>;
  }

  const modalRows =
    openModal === 'scorers' ? getTopScorers(sortedPlayers) :
    openModal === 'assists' ? getTopAssists(sortedPlayers) :
    openModal === 'mvp' ? getTopMVPs(sortedPlayers) :
    openModal === 'mvpCount' ? getTopMvpCount(sortedPlayers) :
    [];

  const modalTitle =
    openModal === 'scorers' ? 'Najbolji strijelci' :
    openModal === 'assists' ? 'Najbolji asistenti' :
    openModal === 'mvpCount' ? 'Igrač utakmice' :
    'Top MVP igraci';
  const modalValueLabel =
    openModal === 'scorers' ? 'Golovi' :
    openModal === 'assists' ? 'Asistencije' :
    openModal === 'mvpCount' ? 'Puta igrač utakmice' :
    'MVP bodovi';
  const modalValue = (p) =>
    openModal === 'scorers' ? p.goals || 0 :
    openModal === 'assists' ? p.assists || 0 :
    openModal === 'mvpCount' ? p.mvpCount || 0 :
    p.mvpPoints || 0;

  return (
    <div className="leaderboard-container">
      <div className="section-header">
        <h2>
          Rang-lista igraca
          {teamData?.name && (
            <span
              style={{
                color: 'var(--text-3)',
                fontWeight: 400,
                marginLeft: 10,
                fontSize: '0.85em',
              }}
            >
              {' '}
              {teamData.name}
            </span>
          )}
        </h2>
      </div>

      <div className="leaderboard-actions">
        <button
          type="button"
          className="leaderboard-action-btn"
          onClick={() => setOpenModal('scorers')}
          disabled={sortedPlayers.length === 0}
        >
          Najbolji strijelci
        </button>
        <button
          type="button"
          className="leaderboard-action-btn"
          onClick={() => setOpenModal('assists')}
          disabled={sortedPlayers.length === 0}
        >
          Najbolji asistenti
        </button>
        <button
          type="button"
          className="leaderboard-action-btn"
          onClick={() => setOpenModal('mvp')}
          disabled={sortedPlayers.length === 0}
        >
          Top MVP igraci
        </button>
        <button
          type="button"
          className="leaderboard-action-btn"
          onClick={() => setOpenModal('mvpCount')}
          disabled={sortedPlayers.length === 0}
        >
          Igrač utakmice
        </button>
      </div>

      <div className="scroll-container">
        <table className="leaderboard-table compact">
          <thead>
            <tr>
              <th>#</th>
              <th>Igrac</th>
              {[
                { key: 'mvpPoints', label: 'MVP', title: 'MVP bodovi' },
                { key: 'goals',        label: 'G',   title: 'Golovi' },
                { key: 'assists',      label: 'A',   title: 'Asistencije' },
                { key: 'mvpCount',  label: 'IU',  title: 'Igrač utakmice - broj puta' },
                { key: 'successPoints',label: 'UxB', title: 'Uspjeh x Bodovi' },
                { key: 'successRate',  label: 'U%',  title: 'Stopa uspjeha' },
                { key: 'points',       label: 'B',   title: 'Bodovi' },
                { key: 'goalDifference',label:'+/-', title: 'Gol razlika' },
                { key: 'goalsFor',     label: 'GF',  title: 'Dani golovi' },
                { key: 'goalsAgainst', label: 'GA',  title: 'Primljeni golovi' },
                { key: 'wins',         label: 'W',   title: 'Pobjede' },
                { key: 'draws',        label: 'D',   title: 'Remiji' },
                { key: 'losses',       label: 'L',   title: 'Porazi' },
                { key: 'matchesPlayed',label: 'P',   title: 'Odigrane utakmice' },
              ].map(({ key, label, title }) => (
                <th
                  key={key}
                  title={title}
                  onClick={() => handleSort(key)}
                  style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                >
                  {label}
                  {sortType === key ? (sortDir === 'desc' ? ' ▼' : ' ▲') : ''}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedPlayers.length > 0 ? (
              sortedPlayers.map((player, index) => (
                <tr key={player.key || player.id}>
                  <td>{index + 1}</td>
                  <td>{player.name}</td>
                  <td>{player.mvpPoints || 0}</td>
                  <td>{player.goals || 0}</td>
                  <td>{player.assists || 0}</td>
                  <td>{player.mvpCount || 0}</td>
                  <td>{player.successPoints || 0}</td>
                  <td>{player.successRate || '0%'}</td>
                  <td>{player.points || 0}</td>
                  <td>{player.goalDifference || 0}</td>
                  <td>{player.goalsFor || 0}</td>
                  <td>{player.goalsAgainst || 0}</td>
                  <td>{player.wins || 0}</td>
                  <td>{player.draws || 0}</td>
                  <td>{player.losses || 0}</td>
                  <td>{player.matchesPlayed || 0}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="16">Nema dostupnih podataka</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {openModal && (
        <div
          className="lb-modal-overlay"
          onClick={() => setOpenModal(null)}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="lb-modal-card"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="lb-modal-header">
              <h3>{modalTitle}</h3>
              <button
                type="button"
                className="lb-modal-close"
                aria-label="Zatvori"
                onClick={() => setOpenModal(null)}
              >
                {'×'}
              </button>
            </div>
            <div className="scroll-container">
              <table className="leaderboard-table compact">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Igrac</th>
                    <th>{modalValueLabel}</th>
                  </tr>
                </thead>
                <tbody>
                  {modalRows.map((player, index) => (
                    <tr key={`${openModal}-${player.key || player.id}`}>
                      <td>{index + 1}</td>
                      <td>{player.name}</td>
                      <td>{modalValue(player)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Leaderboard;
